import React, { useState, useEffect, useRef } from "react";
import LoadingOverlay from "../../../common/LoadingBar.js";
import loader from "../.././../../assets/Image/Loader2.gif";
import { KeyRound, Power } from "lucide-react";

const ManageUserTable = ({
  data,
  columns,
  showAction,
  showFooter,
  onEdit,
  onDelete,
  onToggleAccess,
  onResetPassword,
  showExport,
  showFilter,
  loading,
  currentPage: controlledPage,
  onPageChange,
  totalPages: controlledTotalPages,

  fetchData,
  refreshKey = 0,
  searchData,
}) => {
  const [internalPage, setInternalPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const searchTimer = useRef();

  const currentPage = controlledPage ?? internalPage;
  const setCurrentPage = onPageChange ?? setInternalPage;

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalPages = controlledTotalPages ?? Math.ceil(data.length / rowsPerPage);

  const displayedData = data;

 useEffect(() => {
  if (fetchData && searchTerm !== "") {
    if (searchTimer.current) clearTimeout(searchTimer.current);

    searchTimer.current = setTimeout(() => {
      setCurrentPage(1);
      fetchData({ page: 1, searchTerm });
    }, 500);

    return () => clearTimeout(searchTimer.current);
  } else if (fetchData && searchTerm === "") {

    setCurrentPage(1);
    fetchData({ page: 1, searchTerm: "" });
  }
}, [searchTerm]);

useEffect(() => {
  if (fetchData) {
    fetchData({ page: currentPage, searchTerm });
  }
}, [currentPage, refreshKey]);

  const handleSearchClear = () => {
    setSearchTerm("");
    setCurrentPage(1);
    if (fetchData) {
      fetchData({ page: 1, searchTerm: "" });
    }
  };

  const filterData = (dataToFilter) => {

    if (fetchData) return dataToFilter;

    if (!searchTerm) return dataToFilter;
    return dataToFilter.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  const sortDisplayedData = (dataToSort) => {
    let sortableItems = [...dataToSort];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  };

  const filteredData = filterData(displayedData);
  const sortedDisplayedData = sortDisplayedData(filteredData);

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const renderPagination = () => {
    const pages = [];
    const maxPages = 5;
    let start = Math.max(1, currentPage - Math.floor(maxPages / 2));
    let end = Math.min(totalPages, start + maxPages - 1);
    if (end - start < maxPages) {
      start = Math.max(1, end - maxPages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <a
            className="page-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setCurrentPage(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }
    return pages;
  };

  const statusStyles = {
    enable: {
      backgroundColor: "#198F51",
      color: "#fff",
      borderRadius: "10px",
      padding: "5px 10px",
    },
    disable: {
      backgroundColor: "#FF2024",
      color: "#fff",
      borderRadius: "10px",
      padding: "5px 10px",
    },
  };

  const downloadCSV = () => {
    const headers = columns.map((col) => col.header).join(",") + "\n";
    const rows = data
      .map((row) =>
        columns.map((col) => String(row[col.field] || "")).join(",")
      )
      .join("\n");

    const csvContent = headers + rows;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [showDropdown, setShowDropdown] = useState(false);
  const handleFilterClick = () => {
    setShowDropdown(!showDropdown);
  };

  const renderFilterDropdown = () => {
    return (
      <div className="filter-dropdown" style={dropdownStyles}>
        <label>
          <input type="checkbox" /> Option 1
        </label>
        <label>
          <input type="checkbox" /> Option 2
        </label>
        <label>
          <input type="checkbox" /> Option 3
        </label>
        <button onClick={() => setShowDropdown(false)}>Apply Filter</button>
      </div>
    );
  };

  const dropdownStyles = {
    position: "absolute",
    top: "100%",
    left: "0",
    backgroundColor: "white",
    border: "1px solid #ddd",
    padding: "10px",
    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
    borderRadius: "4px",
    zIndex: "1",
  };

  return (
    <div className="data-table-container">
      <div className="d-flex mb-3 mt-3">
        <div className="expense-searchcontainerstart d-flex">
          <div className="search-container">
            <input
              style={{
                width: "300px",
                height: "45px",
                backgroundColor: "#fff",
                border: "1px solid #DBDBDB",
                boxShadow: "0px 0px 10px rgba(187, 187, 187, 0.25)",
              }}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search"
            />
            <svg
              className="search-container-icon"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5416 19.2497C15.3511 19.2497 19.2499 15.3508 19.2499 10.5413C19.2499 5.73186 15.3511 1.83301 10.5416 1.83301C5.73211 1.83301 1.83325 5.73186 1.83325 10.5413C1.83325 15.3508 5.73211 19.2497 10.5416 19.2497Z"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.1666 20.1663L18.3333 18.333"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          {showExport && (
            <button
              type="button"
              className="btn btn-outline-dark custbtn"
              style={{ borderRadius: "10px", border: "1px solid #f4f4f4" }}
              onClick={downloadCSV}
            >
              <svg
                style={{ marginRight: "5px" }}
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.98975 4.87312L8.90975 2.95312L10.8297 4.87312"
                  stroke="#4A5546"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.91016 10.6353V3.00781"
                  stroke="#4A5546"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 9C3 12.315 5.25 15 9 15C12.75 15 15 12.315 15 9"
                  stroke="#4A5546"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Export To Excel
            </button>
          )}
          {showFilter && (
            <div
              className="filter-button-container"
              style={{ position: "relative" }}
            >
              <button
                className="btn custbtn2"
                type="button"
                onClick={handleFilterClick}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "3px" }}
                >
                  <g opacity="0.8">
                    <path
                      d="M19 3H5C3.586 3 2.879 3 2.44 3.412C2.001 3.824 2 4.488 2 5.815V6.505C2 7.542 2 8.061 2.26 8.491C2.52 8.921 2.993 9.189 3.942 9.723L6.855 11.363C7.491 11.721 7.81 11.9 8.038 12.098C8.512 12.509 8.804 12.993 8.936 13.588C9 13.872 9 14.206 9 14.873V17.543C9 18.452 9 18.907 9.252 19.261C9.504 19.616 9.952 19.791 10.846 20.141C12.725 20.875 13.664 21.242 14.332 20.824C15 20.406 15 19.452 15 17.542V14.872C15 14.206 15 13.872 15.064 13.587C15.1896 13.0042 15.5059 12.4798 15.963 12.097C16.19 11.9 16.509 11.721 17.145 11.362L20.058 9.722C21.006 9.189 21.481 8.922 21.74 8.492C22 8.062 22 7.542 22 6.504V5.814C22 4.488 22 3.824 21.56 3.412C21.122 3 20.415 3 19 3Z"
                      stroke="white"
                      strokeWidth="1.5"
                    />
                  </g>
                </svg>
                Filter
              </button>
              {showDropdown && renderFilterDropdown()}
            </div>
          )}
        </div>

        <div>{}</div>
      </div>

      <table
        className="data-table custom-table"
        style={{
          borderRadius: "20px 20px 0 0",
          border: "1px solid #EBEAF2",
          borderCollapse: "separate",
          borderSpacing: 0,
          overflow: "hidden",
        }}
      >
        <thead className="table-header">
          <tr>
            <th scope="col" className="no-column">
              No
            </th>
            {columns.map((col, index) => (
              <th key={index} scope="col" onClick={() => handleSort(col.field)}>
                {col.header}
                {sortConfig.key === col.field &&
                  (sortConfig.direction === "ascending" ? (
                    <svg
                      width="8"
                      height="4"
                      viewBox="0 0 8 4"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: "5px" }}
                    >
                      <path d="M0 4L4 0L8 4H0Z" fill="#000000" />
                    </svg>
                  ) : (
                    <svg
                      width="8"
                      height="4"
                      viewBox="0 0 8 4"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: "5px" }}
                    >
                      <path d="M0 0L4 4L8 0H0Z" fill="#000000" />
                    </svg>
                  ))}
              </th>
            ))}
            {showAction && (
              <th scope="col" className="action-column">
                Action
              </th>
            )}
          </tr>
        </thead>
        <tbody className="table-body">
          {sortedDisplayedData.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (showAction ? 2 : 1)}
                style={{
                  textAlign: "center",
                  fontWeight: "600",
                  fontSize: "24px",
                  background:
                    "linear-gradient(180deg, rgba(255, 255, 255, 0.1) -92.86%, #4A5546 71.43%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {loading ? (
      <LoadingOverlay loading={loading} loader={loader} />
                ) : (
                  "No data found"
                )}
              </td>
            </tr>
          ) : (
            sortedDisplayedData.map((item) => (
              <tr key={item.id || item.index}>
                <td>
                  {(currentPage - 1) * rowsPerPage +
                    sortedDisplayedData.indexOf(item) +
                    1}
                </td>
                {columns.map((col, colIndex) => (
                  <td key={colIndex}>
                    {col.field === "is_active" ? (
                      <div
                        style={{
                          ...item.is_active === false
                            ? statusStyles.disable
                            : statusStyles.enable,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        {item.is_active === false ? "Disabled" : "Enabled"}
                      </div>
                    ) : col.field === "joining_date" ? (
                      new Date(item.joining_date).toLocaleDateString("en-US")
                    ) : item[col.field] !== undefined &&
                      item[col.field] !== null ? (
                      typeof item[col.field] === "function"
                        ? item[col.field]()
                        : item[col.field]
                    ) : (
                      "-"
                    )}
                  </td>
                ))}
                {showAction && (
                  <td className="action-column">
                    <button className="action-button" onClick={() => onEdit(item)}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.8067 4.695C14.0667 4.435 14.0667 4.00167 13.8067 3.755L12.2467 2.195C12 1.935 11.5667 1.935 11.3067 2.195L10.08 3.415L12.58 5.915M2 11.5017V14.0017H4.5L11.8733 6.62167L9.37333 4.12167L2 11.5017Z"
                          fill="#000000"
                        />
                      </svg>
                    </button>
                    {item.ms_role_name?.trim().toLowerCase() === "store employee" && (
                      <>
                        <button
                          type="button"
                          className={`action-button employee-access-action ${item.is_active === false ? "enable" : "disable"}`}
                          onClick={() => onToggleAccess?.(item)}
                          title={item.is_active === false ? "Enable employee access" : "Disable employee access"}
                          aria-label={item.is_active === false ? "Enable employee access" : "Disable employee access"}
                        >
                          <Power size={17} />
                        </button>
                        <button
                          type="button"
                          className="action-button employee-reset-action"
                          onClick={() => onResetPassword?.(item)}
                          title="Send password reset link"
                          aria-label="Send password reset link"
                        >
                          <KeyRound size={17} />
                        </button>
                      </>
                    )}
                    {onDelete && (
                      <button className="action-button" onClick={() => onDelete(item.id)} title="Delete user">
                        <span aria-hidden="true">×</span>
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {showFooter && (
        <nav>
          <ul className="pagination">{renderPagination()}</ul>
        </nav>
      )}
    </div>
  );
};

export default ManageUserTable;
